import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import type { AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { fileUtils, SocketEventNamespace, sortUtils } from '@core';
import { IncidentColumn, incidentColumnTitle, type IncidentModel, IncidentStatus, type NormalizedIncident, normalizeIncidents } from '@models';
import { SocketEvents } from '@ngrx-store';
import { IncidentsFilterService } from '@services';
import { CdkVirtualScrollViewportWrapperComponent, NoDataPlaceholderComponent, TableZoomComponent } from '@ui';
import { filter } from 'rxjs';

import { IncidentSeverityIconComponent } from '../../incident-status';
import { NotesDialogComponent } from '../../notes-dialog';

const ROW_HEIGHT = 44;

enum IncidentTabStatusFilter {
  OPEN = 'open',
  HISTORY = 'history',
}

export const staticIncidentColumnTitle: Record<string, string> = {
  [IncidentColumn.INDEX]: '№',
  [IncidentColumn.TICKET_NUMBER]: 'Ticket №',
};

@Component({
  imports: [
    MatTable,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatSort,
    RouterLink,
    MatHeaderCellDef,
    NgClass,
    NoDataPlaceholderComponent,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    TableZoomComponent,
    MatIcon,
    CdkVirtualScrollViewportWrapperComponent,
    IncidentSeverityIconComponent,
  ],
  selector: 'app-incidents-table',
  styleUrls: ['./incidents-table.component.scss'],
  templateUrl: './incidents-table.component.html',
})
export class IncidentsTableComponent implements AfterViewInit, OnChanges {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() noDataText = 'Data is Empty';
  @Input() noResolvedInicidentsText = 'No resolved incidents for selected period';
  @Input() isNoDataBigSize = false;
  @Input() incidentsData: IncidentModel[] = [];
  @Input() isReportMode = false;
  @Input() isInjected = false;
  @Input() dataIsLoading = false;
  @Input() isMobile = false;
  @Input() tableId = '';
  incidentTabStatusFilterEnum = IncidentTabStatusFilter;
  dataSource = new MatTableDataSource<NormalizedIncident>();
  dataSourceIndex: { [id: string]: number; } = {};
  public incidentsFilterService = inject(IncidentsFilterService);
  readonly staticIncidentColumnTitle = staticIncidentColumnTitle;
  readonly incidentColumnTitle = incidentColumnTitle;
  readonly IncidentColumn = IncidentColumn;

  protected zoom = signal<string | null>(null);
  protected TYPICAL_COL_HEIGHT = ROW_HEIGHT;
  protected readonly eIncidentStatus = IncidentStatus;
  private changeDetectionRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  constructor() {
    this.incidentsFilterService.filteredIncidents$.pipe(takeUntilDestroyed()).subscribe(incidents => {
      this.initTableData(incidents);
    });

    SocketEvents
      .pipe(
        filter(({ type }) => type.startsWith(SocketEventNamespace.INCIDENTS)),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.incidentsFilterService.refreshIncidentCounter());
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.changeDetectionRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['incidentsData']) {
      this.initTableData(normalizeIncidents(changes['incidentsData'].currentValue));
      this.getTableIndexes(this.dataSource.data);

    }
  }

  getTableIndexes(incidents: NormalizedIncident[]) {
    this.dataSourceIndex = {};
    for (let i = 0; i < incidents.length; i++) {
      this.dataSourceIndex[incidents[i].id] = i + 1;
    }
  }

  initTableData(incidents: NormalizedIncident[]) {
    this.dataSource.data = incidents;
    this.dataSource.sort = this.sort;
    this.dataSource.sortData = sortUtils.naturalSorting;
    this.getTableIndexes(incidents);
  }

  prepareIncidentsDataForDownloading() {
    const columns = this.incidentsFilterService.displayedColumns.map(column => (column === 'index' ? 'id' : column));

    return fileUtils.prepareDataForDownloading(columns, this.dataSource.filteredData);
  }

  exportIncidentsToCSV() {
    fileUtils.exportDataToCSV(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  exportIncidentsToJSON() {
    fileUtils.exportDataToJSON(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  protected openNotesDialog(incident: IncidentModel) {
    this.dialog.open(NotesDialogComponent, {
      // TODO: move common settings for backdrop modal to root app provider config
      backdropClass: 'backdrop-modal-panel',
      data: { incident },
    });
  }

  protected trackBy(_index: number, el: NormalizedIncident) {
    return el.id;
  }
}
